/* eslint-disable unicorn/filename-case -- TODO rename later */
import { clientSDK } from "@istari-frontend/api-client";
import { underscoreToSpaced } from "@istari-frontend/shared";
import {
  AuthIntegration,
  AuthIntegrationType,
  AuthIntegrationUpdate,
  FunctionAuthType,
  NewAuthIntegration,
} from "@istari/istari-client";
import { useMutation, useQuery, UseQueryResult } from "@tanstack/react-query";

import { lookupOrFetchUser } from "~/api/useModels";
import {
  Oauth1Config,
  OidcConfig,
  toOauth1Config,
  toOidcConfig,
} from "~/connections/data-source/adapter/to-integration-config";
import { toOauth1ProviderData } from "~/connections/data-source/adapter/to-oauth1-provider-data";
import { toOidcProviderData } from "~/connections/data-source/adapter/to-oidc-provider-data";
import { Oauth1ProviderConfig } from "~/connections/model/oauth1-provider-config";
import { OidcProviderConfig } from "~/connections/model/oidc-provider-config";
import { toControlTagSummaries } from "~/control-tags/data-source/adapter/to-control-tag-summary";
import { ControlTagSummary } from "~/control-tags/model/control-tag";

// The SDK's list endpoint accepts no sort; keep the key to what is actually sent so it can't imply
// an ordering that isn't applied.
export interface IntegrationListOptions {
  page: number;
  size: number;
}

export type AuthInfoType = { authIntegrationTypes: AuthIntegrationType[]; key: string };

export type { Oauth1Config, OidcConfig } from "~/connections/data-source/adapter/to-integration-config";

export interface AuthIntData {
  id: string;
  authIntegrationType: AuthIntegrationType;
  authType: FunctionAuthType;
  createdAt: Date;
  createdBy: string;
  appId?: string | null;
  name: string;
  oidc?: OidcConfig;
  oauth1?: Oauth1Config;
  // Non-secret app/link config (e.g. Teamwork Cloud `server_name`), merged into a linked file's
  // metadata at connect time. Admin-set; null until configured.
  integrationData: Record<string, string> | null;
  // Users must hold every one of these to see or use the integration (empty = unrestricted).
  // Enforcement is server-side; the client only displays the rule (same note on Connection).
  controlTags: ControlTagSummary[];
}

// A single AuthIntegration presented as a one-element application group.
export interface AppIntData {
  id: string;
  integrationType: AuthIntegrationType;
  integrationData: Record<string, string> | null;
  createdAt: Date;
  creatorName?: string;
  authIntegrations: AuthIntData[];
  name: string;
}

export interface AppIntPageData {
  items: AppIntData[];
  total: number;
}

const appIntegrationsKey = ["listAppIntegrations"];
const authIntegrationsKey = ["listAuthIntegrations"];

export const functionAuthQueryKeys = {
  appIntegrations: appIntegrationsKey,
  authIntegrations: authIntegrationsKey,
  listAppIntegrations: (filter: IntegrationListOptions) => [...appIntegrationsKey, filter],
  listAuthIntegrations: (filter: IntegrationListOptions) => [...authIntegrationsKey, filter],
};

// The SDK types the field's values as `any`; keep only the entries a validated narrow proves are
// strings rather than blindly casting the whole record.
function toIntegrationData(value: Record<string, unknown> | null | undefined): Record<string, string> | null {
  if (!value) return null;
  return Object.fromEntries(
    Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === "string"),
  );
}

async function authApiToTableData(authInt: AuthIntegration): Promise<AuthIntData> {
  return {
    id: authInt.id,
    authIntegrationType: authInt.authIntegrationType,
    authType: authInt.authType,
    createdAt: authInt.created,
    createdBy: (await lookupOrFetchUser(authInt.createdById)) ?? authInt.createdById,
    appId: authInt.appIntegrationId,
    integrationData: toIntegrationData(authInt.integrationData),
    name: authInt.name ?? underscoreToSpaced(authInt.authIntegrationType),
    controlTags: toControlTagSummaries(authInt.controlTags),
    oidc: authInt.oidcProviderData ? toOidcConfig(authInt.oidcProviderData) : undefined,
    oauth1: authInt.oauth1ProviderData ? toOauth1Config(authInt.oauth1ProviderData) : undefined,
  };
}

async function authToAppData(authInt: AuthIntegration): Promise<AppIntData> {
  return {
    id: authInt.id,
    integrationType: authInt.authIntegrationType,
    // Non-secret app/link config (e.g. Teamwork Cloud `server_name`) merged into a linked file's metadata.
    integrationData: toIntegrationData(authInt.integrationData),
    createdAt: authInt.created,
    creatorName: await lookupOrFetchUser(authInt.createdById),
    authIntegrations: [await authApiToTableData(authInt)],
    // Prefer the admin-assigned name so multiple integrations of one provider stay distinguishable;
    // fall back to the humanized provider type when no name was set.
    name: authInt.name?.trim() || underscoreToSpaced(authInt.authIntegrationType),
  };
}

export const useListAppIntegrations = ({ page, size }: IntegrationListOptions): UseQueryResult<AppIntPageData> =>
  useQuery({
    queryKey: functionAuthQueryKeys.listAppIntegrations({ page, size }),
    queryFn: () =>
      clientSDK.listAuthIntegrations(undefined, undefined, undefined, page, size).then(async (authPage) => ({
        items: await Promise.all(authPage.items.map((item) => authToAppData(item))),
        total: authPage.total,
      })),
  });

export interface AuthIntPageData {
  items: AuthIntData[];
  total: number;
}

// A save payload carries at most one standard's provider config; the union keeps a both-set
// payload unrepresentable (the secret-entry standards carry neither).
export type NewIntegrationProviderConfig =
  | { oidc: OidcProviderConfig; oauth1?: never }
  | { oidc?: never; oauth1: Oauth1ProviderConfig }
  | { oidc?: never; oauth1?: never };

export type NewAuthIntegrationData = NewIntegrationProviderConfig & {
  authIntegrationType: AuthIntegrationType;
  authType: FunctionAuthType;
  name?: string;
  // The provider client secret — for OAuth 1.0a integrations this carries the consumer secret.
  clientSecret?: string;
  // Non-secret app/link config (e.g. { server_name } for Teamwork Cloud).
  integrationData?: Record<string, string>;
  controlTagIds: string[];
};

export const useListAuthIntegrations = ({ page, size }: IntegrationListOptions): UseQueryResult<AuthIntPageData> =>
  useQuery({
    queryKey: functionAuthQueryKeys.listAuthIntegrations({ page, size }),
    queryFn: () =>
      clientSDK.listAuthIntegrations(undefined, undefined, undefined, page, size).then(async (authPage) => ({
        items: await Promise.all(authPage.items.map((item) => authApiToTableData(item))),
        total: authPage.total,
      })),
  });

export const useArchiveAuthIntegration = (options?: {
  onSuccess?: (id: string) => void;
  onError?: (error: unknown) => void;
}) =>
  useMutation({
    mutationFn: (authIntegrationId: string) => clientSDK.archiveAuthIntegration(authIntegrationId).then((r) => r.id),
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });

export const useAddAuthIntegration = (options?: { onSuccess?: () => void; onError?: (error: unknown) => void }) =>
  useMutation({
    mutationFn: async (data: NewAuthIntegrationData) => {
      // The provider config lives on the integration itself (oidcProviderData + client secret); the
      // uploaded "manifest" is a small placeholder so the integration has a backing file revision.
      const manifest = new File(
        [JSON.stringify({ authIntegrationType: data.authIntegrationType, authType: data.authType }, null, 2)],
        "registration.json",
        { type: "application/json" },
      );
      const revision = await clientSDK.createRevision({ file: manifest });
      // Assign the optional fields by name onto the instance: positional args would misroute on
      // an SDK regen reorder, and an untyped Object.assign would silently drop them on a rename —
      // worst case the client secret never reaching the backend.
      const request = new NewAuthIntegration(data.authIntegrationType, data.authType, revision);
      request.name = data.name || undefined;
      request.oidcProviderData = data.oidc ? toOidcProviderData(data.oidc) : undefined;
      request.oauth1ProviderData = data.oauth1 ? toOauth1ProviderData(data.oauth1) : undefined;
      request.clientSecret = data.clientSecret || undefined;
      request.integrationData = data.integrationData;
      request.controlTagIds = data.controlTagIds;
      return clientSDK.createAuthIntegration(request);
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });

export const useUpdateAuthIntegration = (options?: { onSuccess?: () => void; onError?: (error: unknown) => void }) =>
  useMutation({
    mutationFn: async ({ id, data }: { id: string; data: NewAuthIntegrationData }) => {
      // Assign by name (see useAddAuthIntegration). Only re-seal the client secret when one was
      // entered; an empty field leaves the stored secret intact.
      const update = new AuthIntegrationUpdate(data.authIntegrationType, data.authType);
      update.name = data.name ?? undefined;
      update.oidcProviderData = data.oidc ? toOidcProviderData(data.oidc) : undefined;
      update.oauth1ProviderData = data.oauth1 ? toOauth1ProviderData(data.oauth1) : undefined;
      update.clientSecret = data.clientSecret || undefined;
      update.integrationData = data.integrationData;
      update.controlTagIds = data.controlTagIds;
      await clientSDK.updateAuthIntegration(id, update);
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
